// Design-language status treatment for workflow node cards (Figma "8. Workflow
// New"). The engine vocabulary (pending/running/completed/waiting_for_user/…)
// maps onto the card labels the design uses: Ready · Thinking... · Success ·
// Need Confirmation · Failed to Execute · Skipped. Pure and unit-tested.

export type NodeStatusTone =
  | 'ready'
  | 'thinking'
  | 'success'
  | 'confirm'
  | 'failed'
  | 'skipped'

export interface NodeStatusDisplay {
  /** Card footer text, rendered as "Status: <label>" (the en default). */
  label: string
  /** i18n key for the label — render via t(labelKey, { defaultValue: label }). */
  labelKey: string
  tone: NodeStatusTone
}

/** Engine statuses that read as a hard failure on a node card. */
const FAILED_STATUSES = new Set([
  'failed',
  'blocked',
  'guardrail_stopped',
  'budget_limited',
  'error',
  'cancelled',
])

/**
 * Map an engine step/run status onto the node-card treatment. Unknown statuses
 * fall back to Ready (the neutral card) rather than inventing a color.
 */
export function nodeStatusDisplay(status?: string): NodeStatusDisplay {
  switch (status) {
    case 'running':
      return { label: 'In Progress', labelKey: 'workflow:builder.status_in_progress', tone: 'thinking' }
    case 'completed':
    case 'ok':
    case 'success':
      return { label: 'Success', labelKey: 'workflow:builder.status_success', tone: 'success' }
    case 'completed_with_unknowns':
      return {
        label: 'Success · open questions',
        labelKey: 'workflow:builder.status_success_open',
        tone: 'confirm',
      }
    case 'waiting_for_user':
    case 'awaiting_approval':
      return {
        label: 'Need Confirmation',
        labelKey: 'workflow:builder.status_confirm',
        tone: 'confirm',
      }
    case 'skipped':
      return { label: 'Skipped', labelKey: 'workflow:builder.status_skipped', tone: 'skipped' }
    default:
      if (status && FAILED_STATUSES.has(status)) {
        return { label: 'Failed to Execute', labelKey: 'workflow:builder.status_failed', tone: 'failed' }
      }
      return { label: 'Ready', labelKey: 'workflow:builder.status_ready', tone: 'ready' }
  }
}

/** Tailwind classes per tone for the node card: border, header tint, footer text. */
export const NODE_TONE_CLASSES: Record<
  NodeStatusTone,
  { border: string; header: string; footerText: string }
> = {
  ready: { border: 'border-border', header: '', footerText: 'text-muted-foreground' },
  // Running/in-progress reads ORANGE (Putra 2026-07-15) — distinct from the
  // amber "Need Confirmation" tone, and matching the node's loading bar.
  thinking: {
    border: 'border-orange-400 dark:border-orange-600',
    header: 'bg-orange-50 dark:bg-orange-950/40',
    footerText: 'text-orange-600 dark:text-orange-500',
  },
  success: {
    border: 'border-green-400 dark:border-green-700',
    header: 'bg-green-50 dark:bg-green-950/40',
    footerText: 'text-green-600 dark:text-green-500',
  },
  confirm: {
    border: 'border-amber-400 dark:border-amber-600',
    header: '',
    footerText: 'text-amber-600 dark:text-amber-500',
  },
  failed: {
    border: 'border-red-400 dark:border-red-700',
    header: 'bg-red-50 dark:bg-red-950/40',
    footerText: 'text-red-600 dark:text-red-500',
  },
  skipped: { border: 'border-border', header: '', footerText: 'text-muted-foreground' },
}
